import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import StatusBadge from './StatusBadge';

const AssetDataGrid = ({ assets, selectedAssets, onSelectionChange, onAssetClick, onBulkAction }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedAndFilteredAssets = useMemo(() => {
    let filteredAssets = assets?.filter(asset =>
      Object.values(asset)?.some(value =>
        value?.toString()?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      )
    );

    if (sortConfig?.key) {
      filteredAssets?.sort((a, b) => {
        const aValue = a?.[sortConfig?.key];
        const bValue = b?.[sortConfig?.key];
        
        if (aValue < bValue) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredAssets;
  }, [assets, sortConfig, searchQuery]);

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(sortedAndFilteredAssets?.map(asset => asset?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectAsset = (assetId, checked) => {
    if (checked) {
      onSelectionChange([...selectedAssets, assetId]);
    } else {
      onSelectionChange(selectedAssets?.filter(id => id !== assetId));
    }
  };

  const isAllSelected = sortedAndFilteredAssets?.length > 0 && 
    sortedAndFilteredAssets?.every(asset => selectedAssets?.includes(asset?.id));
  const isIndeterminate = selectedAssets?.length > 0 && !isAllSelected;

  const columns = [
    { key: 'assetId', label: 'Asset ID', sortable: true },
    { key: 'type', label: 'Type', sortable: true },
    { key: 'owner', label: 'Owner', sortable: true },
    { key: 'purchaseDate', label: 'Purchase Date', sortable: true },
    { key: 'warrantyExpiration', label: 'Warranty Expiration', sortable: true },
    { key: 'status', label: 'Status', sortable: true }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      {/* Search and Bulk Actions */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex-1 max-w-md">
            <Input
              type="search"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full"
            />
          </div>
          
          {selectedAssets?.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                {selectedAssets?.length} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('status')}
                iconName="Edit"
                iconPosition="left"
              >
                Update Status
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('export')}
                iconName="Download"
                iconPosition="left"
              >
                Export CSV
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Data Grid */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-12 p-4">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              {columns?.map((column) => (
                <th key={column?.key} className="text-left p-4">
                  {column?.sortable ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort(column?.key)}
                      className="h-auto p-0 font-medium text-foreground hover:text-primary"
                    >
                      <span>{column?.label}</span>
                      {sortConfig?.key === column?.key && (
                        <Icon
                          name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                          size={16}
                          className="ml-1"
                        />
                      )}
                    </Button>
                  ) : (
                    <span className="font-medium text-foreground">{column?.label}</span>
                  )}
                </th>
              ))}
              <th className="w-12 p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedAndFilteredAssets?.map((asset) => (
              <tr
                key={asset?.id}
                className="hover:bg-muted/30 transition-smooth cursor-pointer"
                onClick={() => onAssetClick(asset)}
              >
                <td className="p-4" onClick={(e) => e?.stopPropagation()}>
                  <Checkbox
                    checked={selectedAssets?.includes(asset?.id)}
                    onChange={(e) => handleSelectAsset(asset?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-4">
                  <span className="font-mono text-sm font-medium text-primary">
                    {asset?.assetId}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Package" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium">{asset?.type}</span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="User" size={16} className="text-muted-foreground" />
                    <span className="text-sm">{asset?.owner}</span>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(asset?.purchaseDate)}
                  </span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(asset?.warrantyExpiration)}
                  </span>
                </td>
                <td className="p-4">
                  <StatusBadge status={asset?.status} size="sm" />
                </td>
                <td className="p-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onAssetClick(asset);
                    }}
                  >
                    <Icon name="ChevronRight" size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedAndFilteredAssets?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Search" size={32} className="text-muted-foreground mb-2 mx-auto" />
          <p className="text-muted-foreground">No assets found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default AssetDataGrid;